import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Rss } from 'lucide-react';
import { SOCIAL_LINKS, trackSocialClick } from '../lib/socialLinks';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: 'Quick Links',
      links: [
        { name: 'Home', url: '/' },
        { name: 'Latest News', url: '/news' },
        { name: 'Paisley Events', url: '/events' },
        { name: 'Paisley History', url: '/history' }
      ]
    },
    {
      title: 'Explore',
      links: [
        { name: 'Visitor Attractions', url: '/attractions' },
        { name: 'Famous People', url: '/famous-people' },
        { name: 'Roads & Travel', url: '/roads-and-travel' },
        { name: 'Contact', url: '/contact' }
      ]
    },
    {
      title: 'Community',
      links: [
        { name: 'Support Paisley', url: '/community' },
        { name: 'Local Business', url: '/directory' },
        { name: 'Become a Sponsor', url: '/sponsors' }
      ]
    },
    {
      title: 'Partner With Us',
      links: [
        { name: 'Sponsorship Packages', url: '/sponsors' },
        { name: 'Business Directory', url: 'https://paisleyscotland.com/directory', sameTab: true },
        { name: 'Advertise With Us', url: 'https://paisleyscotland.com/directory', sameTab: true },
        { name: 'Partnership Enquiries', url: '/contact' }
      ]
    }
  ];

  return (
    <footer className="bg-[var(--stone)] text-gray-300">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_paisley-org-clone/artifacts/6pjbshkc_paisley-logo.webp"
                alt="Paisley.org.uk"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              Your intelligent guide to Paisley, Renfrewshire. Explore history, events, attractions, and community services.
            </p>

            {/* Social icons — sits inside the brand column so the outlets are
                visually tied to the paisley.org.uk identity. */}
            <div
              className="flex items-center gap-2 flex-wrap"
              data-testid="footer-social-links"
              aria-label="Paisley.org.uk on social media"
            >
              {SOCIAL_LINKS.map(({ name, url, Icon }) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="me noopener noreferrer"
                  aria-label={`Paisley.org.uk on ${name}`}
                  title={name}
                  data-testid={`footer-social-${name.toLowerCase()}`}
                  onClick={() => trackSocialClick(name, 'footer')}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-white/5 text-gray-300 hover:text-white hover:bg-[var(--madder)] transition-all duration-200"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-white font-semibold mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => {
                  const isInternal = link.url.startsWith('/');
                  const newTab = !isInternal && !link.sameTab;
                  if (isInternal) {
                    return (
                      <li key={link.name}>
                        <Link
                          to={link.url}
                          className="text-sm text-gray-400 hover:text-[var(--madder)] transition-colors duration-200 flex items-center gap-1 group"
                        >
                          {link.name}
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={link.name}>
                      <a
                        href={link.url}
                        {...(newTab && { target: '_blank', rel: 'noopener noreferrer' })}
                        className="text-sm text-gray-400 hover:text-[var(--madder)] transition-colors duration-200 flex items-center gap-1 group"
                      >
                        {link.name}
                        {newTab && <ExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity" size={12} />}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {currentYear} Paisley.org.uk. All rights reserved.
            </p>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <Link
                to="/privacy"
                className="text-sm text-gray-500 hover:text-[var(--madder)] transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-500 hover:text-[var(--madder)] transition-colors"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('open-cookie-notice'))}
                className="text-sm text-gray-500 hover:text-[var(--madder)] transition-colors"
                data-testid="footer-cookie-settings"
              >
                Cookie Settings
              </button>
              <a
                href="/api/rss"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-orange-400 transition-colors"
              >
                <Rss size={14} />
                RSS Feed
              </a>
              <a
                href="/api/sitemap.xml"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-[var(--madder)] transition-colors"
              >
                Sitemap
              </a>
              <a
                href="https://instavibe.co.uk/?utm_source=paisley&utm_medium=footer&utm_campaign=network"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-[var(--madder)] transition-colors underline"
                data-testid="instavibe-footer-link"
              >
                Powered by InstaVibe &mdash; Instagram automation for small business
              </a>
            </div>
            {/* Google Preferred Sources — footer slot. publisher.js (loaded
                globally from index.html) injects the "Add to preferred sources"
                pill into this div at runtime. */}
            <div className="mt-6 flex justify-center">
              <div google-add-preferred-source-btn="" data-testid="gps-footer" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
